import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DeviceService } from '@core/services';
import { TAccountSimpleResponse } from '@project/view-models';
import { Subject, takeUntil } from 'rxjs';
import {
  flattenAccounts,
  TFlattenedAccount,
} from '../../../account-list/account-list/account-list-flatten-account';
import { groupAccountListData } from '../../../account-list/account-list/group-account-list-data';
import { NEW_FOLDER_ID } from '../../../account-list/utils/account-list-constants';
import { AccountListUiLocalStorageService } from '../../../account-list/utils/account-list-ui-local-storage.service';
import { DELETE_COLLAPSED_FOLDER } from '../../../account-list/utils/account-list.ui.store';
import { ESidePanelItem } from '../../../account-list/utils/side-panel-item.enum';
import { PreferencesService } from '../../../preferences/preferences-service/preferences.service';
import { SidePanelExpandAllService } from '../side-panel-expand-all-button/side-panel-expand-all.service';
import { SidePanelSitesListService } from '../side-panel-sites-list.service';

@Component({
  selector: 'pp-side-panel-accounts',
  templateUrl: './side-panel-accounts.component.html',
  styleUrl: './side-panel-accounts.component.scss',
  standalone: false,
})
export class SidePanelAccountsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() ppKeyword: string;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  private readonly destroy$ = new Subject<void>();
  private accounts: TAccountSimpleResponse[] = [];

  flattenedAccounts: TFlattenedAccount[] = [];
  itemHeights: number[] = [];
  ESidePanelItem = ESidePanelItem;
  dragged: boolean;
  isTouchDevice: boolean;
  private previousKeyword = '';

  constructor(
    private sidePanelSitesListService: SidePanelSitesListService,
    private preferencesService: PreferencesService,
    private deviceService: DeviceService,
    private sidePanelExpandAllService: SidePanelExpandAllService,
    private accountListUiLocalStorageService: AccountListUiLocalStorageService,
    private cd: ChangeDetectorRef,
  ) {
    this.sidePanelSitesListService.accountsChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.accounts = response;
        this.refreshList();
      });

    this.sidePanelExpandAllService.collapseChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshList());
    this.isTouchDevice = this.deviceService.isTouchDevice();
  }

  ngOnInit(): void {
    this.accountListUiLocalStorageService.loadPreferencesFromLocalStorage();
    this.sidePanelSitesListService
      .fetchWorkspaces()
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.accounts = accounts;
        this.refreshList();
        requestAnimationFrame(() => this.scrollToActiveWorkspace());
      });
  }

  ngAfterViewInit() {
    this.viewport.checkViewportSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ppKeyword']) {
      this.refreshList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshList(): void {
    const keyword = (this.ppKeyword || '').toLowerCase();
    this.previousKeyword = keyword;

    // 1. Efficient Filter
    const filtered = keyword ? this.filterAccounts(keyword) : this.accounts;

    this.flattenedAccounts = flattenAccounts(
      groupAccountListData(filtered),
      this.preferencesService.getPreferences().favouriteWorkspaceIds,
    );
    this.generateItemHeights();

    // 2. Synchronous update for templates
    this.cd.detectChanges();

    // 3. The "Firefox Triple Poke"
    if (this.viewport) {
      this.viewport.checkViewportSize();
      // Poke after first frame
      requestAnimationFrame(() => {
        this.viewport.checkViewportSize();
        // Poke after sidebar animation usually ends (150ms)
        setTimeout(() => {
          this.viewport.checkViewportSize();
          this.cd.markForCheck();
        }, 150);
      });
    }
  }

  private filterAccounts(keyword: string): TAccountSimpleResponse[] {
    const keywordChanged = keyword !== this.previousKeyword;

    return this.accounts
      .map((account) => {
        const accountNameMatch = account.name.toLowerCase().includes(keyword);
        const workspaces = account.workspaces.filter((ws) => {
          const folder = account.accountFolders.find((f) =>
            f.workspaceIds?.includes(ws.workspaceId),
          );
          return (
            ws.name.toLowerCase().includes(keyword) ||
            (folder?.name || '').toLowerCase().includes(keyword)
          );
        });
        const accountFolders = account.accountFolders.filter((folder) => {
          const containsWorkspace = folder.workspaceIds?.some((id) =>
            account.workspaces
              .find((ws) => ws.workspaceId === id)
              ?.name.toLowerCase()
              .includes(keyword),
          );
          if (containsWorkspace && keywordChanged) {
            DELETE_COLLAPSED_FOLDER(account.id, folder.id);
          }
          return (
            folder.name.toLowerCase().includes(keyword) ||
            containsWorkspace ||
            folder.id === NEW_FOLDER_ID
          );
        });
        return accountNameMatch || workspaces.length || accountFolders.length
          ? { ...account, workspaces, accountFolders }
          : null;
      })
      .filter((a) => !!a) as TAccountSimpleResponse[];
  }

  private generateItemHeights(): void {
    const currentUrl = window.location.href;
    this.itemHeights = this.flattenedAccounts.map((item) => {
      switch (item.type) {
        case ESidePanelItem.ACCOUNT:
          return 43;
        case ESidePanelItem.WORKSPACE:
          return currentUrl.includes(item.workspace.workspaceId) ? 34 : 26;
        case ESidePanelItem.NEW_SITE_BUTTON:
          return 28;
        case ESidePanelItem.FOLDER:
        case ESidePanelItem.FOLDER_EMPTY:
        case ESidePanelItem.FAVOURITES_EMPTY:
          return 26;
        case ESidePanelItem.END_LINE:
        case ESidePanelItem.FAVOURITES:
          return 34;
        default:
          return 0;
      }
    });
  }

  private scrollToActiveWorkspace(): void {
    if (!this.viewport || this.flattenedAccounts.length === 0) {
      return;
    }
    const activeIndex = this.flattenedAccounts.findIndex(
      (item) => item.type === ESidePanelItem.WORKSPACE && window.location.href.includes(item.id),
    );
    if (activeIndex !== -1) {
      this.viewport.scrollToIndex(activeIndex, 'auto');
    }
  }

  drop(event: CdkDragDrop<TFlattenedAccount[], any, any>): void {
    this.fixFirefoxClickEvent();
    const droppedElementHTML = document.elementFromPoint(event.dropPoint.x, event.dropPoint.y);
    if (!droppedElementHTML) {
      return;
    }
    const droppedElementItem = droppedElementHTML.closest('.cdk-drag');
    const movedWorkspace = {
      accountId: event.item.data.accountId,
      workspaceId: event.item.data.workspace.workspaceId,
      folderId: event.item.data.folderId,
    };
    if (!droppedElementItem) {
      this.sidePanelSitesListService.removeSiteFromFolder(
        movedWorkspace.accountId,
        movedWorkspace.workspaceId,
        movedWorkspace.folderId,
      );
      return;
    }
    const targetAccountId = droppedElementItem.getAttribute('accountId');
    if (movedWorkspace.accountId !== targetAccountId) {
      if (movedWorkspace.folderId) {
        this.sidePanelSitesListService.removeSiteFromFolder(
          movedWorkspace.accountId,
          movedWorkspace.workspaceId,
          movedWorkspace.folderId,
        );
      }
      return;
    }
    const newFolderId = droppedElementItem.getAttribute('folderId');
    if (newFolderId === movedWorkspace.folderId) {
      return;
    }
    if (newFolderId) {
      this.sidePanelSitesListService.addSiteToFolder(
        movedWorkspace.accountId,
        movedWorkspace.workspaceId,
        newFolderId,
      );
    } else {
      this.sidePanelSitesListService.removeSiteFromFolder(
        movedWorkspace.accountId,
        movedWorkspace.workspaceId,
        movedWorkspace.folderId,
      );
    }
  }

  private fixFirefoxClickEvent(): void {
    this.dragged = true;
    setTimeout(() => (this.dragged = false), 100);
  }

  updateList(): void {
    this.refreshList();
  }
  trackByFunction(index: number, item: TFlattenedAccount): string {
    return item.id;
  }
}
